import { useState } from 'react';
import { AccordionItem } from './accordion';

interface FaqEntry {
  question: string;
  answer: string;
}

interface FaqAccordionProps {
  title: string;
  items: Array<FaqEntry>;
  /** Below `sm`, scale the heading/gaps/card down to the app landing's mobile
   *  design (Figma node 5663:15427) instead of using the desktop metrics at
   *  every width. Opt-in, because the merchant landing has no mobile FAQ
   *  design yet and keeps the desktop sizing throughout. */
  mobileFluid?: boolean;
}

// Fluid values scale linearly with viewport width between the two reference
// mockups — 402px (5663:15427) and 1440px (5210:11418, already verified
// against the design) — via the standard `clamp(min, intercept + slope*vw,
// max)` formula, instead of jumping at a single breakpoint.
const FLUID_OUTER_GAP = 'gap-[clamp(16px,6.71px+2.312vw,40px)]';
const FLUID_LIST_GAP = 'gap-[clamp(12px,10.45px+0.385vw,16px)]';
const FLUID_H2_SIZE = 'text-[clamp(20px,15.35px+1.156vw,32px)]';
const FLUID_H2_LEADING = 'leading-[clamp(28px,23.35px+1.156vw,40px)]';

/** Presentational FAQ block: heading + single-open accordion list. Content is
 *  supplied by props so both landing pages can reuse it. */
export function FaqAccordion({ title, items, mobileFluid = false }: FaqAccordionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <div className={`flex flex-col ${mobileFluid ? FLUID_OUTER_GAP : 'gap-10'}`}>
      <h2
        className={`text-ink font-semibold tracking-tight ${
          mobileFluid ? `${FLUID_H2_SIZE} ${FLUID_H2_LEADING}` : 'text-h2'
        }`}
      >
        {title}
      </h2>
      <div className={`flex flex-col ${mobileFluid ? FLUID_LIST_GAP : 'gap-4'}`}>
        {items.map((item, index) => (
          <AccordionItem
            key={item.question}
            question={item.question}
            answer={item.answer}
            open={openIndex === index}
            onToggle={() => setOpenIndex((current) => (current === index ? null : index))}
            mobileFluid={mobileFluid}
          />
        ))}
      </div>
    </div>
  );
}
